import type { ColumnSchema, QueryError, ResultRow } from '@/types/api';
import type { GroupState } from '@/components/group/groupModel';

/**
 * Where the grid's current view is being computed.
 *
 *  local         -- answered by DuckDB-WASM in this tab. No network. The goal.
 *  remote        -- the result was too large to cache, so every sort/filter is a
 *                   round trip. Always surfaced in the UI; see spec §4.7.
 *  materializing -- loading rows into the browser table.
 *  unavailable   -- WASM could not start (no cross-origin isolation, blocked
 *                   worker, old browser). Behaves as `remote` but says why.
 *  idle          -- nothing loaded yet.
 */
export type GridMode = 'idle' | 'materializing' | 'local' | 'remote' | 'unavailable';

export type SortDirection = 'asc' | 'desc';

export interface SortSpec {
  readonly column: string;
  readonly direction: SortDirection;
}

export type FilterOperator =
  | 'eq'
  | 'neq'
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte'
  | 'like'
  | 'notLike'
  | 'contains'
  | 'notContains'
  | 'startsWith'
  | 'endsWith'
  | 'isNull'
  | 'isNotNull'
  | 'isEmpty'
  | 'between'
  | 'in'
  | 'notIn';

/** How a filter row joins to the one after it. SQL precedence applies. */
export type FilterJoiner = 'and' | 'or';

export interface FilterSpec {
  readonly column: string;
  readonly operator: FilterOperator;
  /** Unused for isNull/isNotNull/isEmpty; two entries for `between`; n for `in`. */
  readonly values: readonly string[];
  /** Joins this predicate to the next one. Defaults to `and`. */
  readonly joiner?: FilterJoiner;
}

export interface ViewSpec {
  readonly sort: readonly SortSpec[];
  readonly filters: readonly FilterSpec[];
  /**
   * Raw predicate authored in the panel's Text mode. When set it replaces
   * `filters` entirely.
   *
   * This is user-written SQL reaching the query, which is fine: it runs against
   * the user's own cached table in their own browser, and they can already type
   * arbitrary SQL in the editor above. It grants no access they did not have.
   */
  readonly predicate?: string | null;
  /**
   * Group-by plan. When set, the result has a different schema from the query
   * the user wrote, and `ViewResult.columns` carries it.
   */
  readonly group?: GroupState | null;
  readonly offset: number;
  readonly limit: number;
}

export interface ViewResult {
  readonly rows: readonly ResultRow[];
  /** Rows matching the filters, before offset/limit. */
  readonly totalRows: number;
  readonly mode: 'local' | 'remote';
  readonly elapsedMs: number;
  /**
   * Schema of this view when it differs from the query's own -- i.e. when
   * grouped. Null means the grid should use the original result columns.
   */
  readonly columns?: readonly ColumnSchema[] | null;
}

/** A raw query result, as delivered by the SSE stream. */
export interface RawResult {
  readonly queryId: string;
  readonly sql: string;
  readonly columns: readonly ColumnSchema[];
  readonly rows: readonly ResultRow[];
  /** True when the server stopped short of the full result set. */
  readonly truncated: boolean;
}

export interface LocalQueryResult {
  readonly columns: readonly ColumnSchema[];
  readonly rows: readonly ResultRow[];
}

/**
 * The in-browser SQL engine. Abstracted so the cache logic is unit-testable
 * without booting a 30 MB WASM module in jsdom.
 */
export interface LocalEngine {
  materialize(
    table: string,
    columns: readonly ColumnSchema[],
    rows: readonly ResultRow[],
  ): Promise<void>;
  query(sql: string): Promise<LocalQueryResult>;
  drop(table: string): Promise<void>;
  dispose(): Promise<void>;
  /** Writes the table as Parquet and returns the bytes. Optional: only the
   *  real WASM engine can do this, so callers must handle its absence. */
  exportParquet?(table: string): Promise<Uint8Array>;
}

/** Executes a rewritten query against the backend, for the >N-rows fallback. */
export interface RemoteExecutor {
  run(sql: string, signal?: AbortSignal): Promise<LocalQueryResult>;
}

export interface CacheConfig {
  /** Rows above which we stop caching locally and fall back to the server. */
  readonly maxRows: number;
}

export const DEFAULT_MAX_CACHE_ROWS = 100_000;

export interface CacheStatus {
  readonly mode: GridMode;
  readonly cachedRows: number;
  /** Present when mode is `remote` or `unavailable`. Shown verbatim in the UI. */
  readonly reason: string | null;
  readonly lastError: QueryError | null;
}
